import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import ShareOutlined from '@mui/icons-material/ShareOutlined';

import ShareDialog from '@/features/_shared/ShareDialog';
import { normalizeSx } from '@/shared/lib/normalizeSx';
import Button from '@/shared/ui/primitives/Button';

import useShareButton from './useShareButton';

import type { Sx } from '@/shared/types/uiTypes';

const buttonBaseSx: Sx = { width: 192, height: 48, fontSize: '16px' };

const ShareButton = ({
  title,
  additionalTitle,
  shareUrl: shareUrlOverride,
  sx,
  iconSize = 24
}: {
  title?: string;
  additionalTitle?: string;
  shareUrl?: string;
  sx?: Sx;
  iconSize?: number;
}) => {
  const {
    modalOpen,
    setModalOpen,
    content,
    setContent,
    recipient,
    handleChangeRecipient,
    handleCopyToClipboard,
    sendEmail,
    emailError,
    shareUrl
  } = useShareButton(undefined, shareUrlOverride);
  const headerSuffix = additionalTitle ? ` / ${additionalTitle}` : '';
  const header = `Share ${title}${headerSuffix}`;

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <>
      <Button
        color='secondary'
        sx={[buttonBaseSx, ...normalizeSx(sx)]}
        variant='contained'
        onClick={() => {
          setModalOpen(true);
        }}
      >
        <ShareOutlined
          sx={{
            width: iconSize,
            height: iconSize
          }}
        />
        Share
      </Button>
      <ShareDialog
        content={content}
        emailError={emailError}
        handleChangeRecipient={handleChangeRecipient}
        header={header}
        open={modalOpen}
        recipient={recipient}
        shareUrl={shareUrl}
        onClose={handleClose}
        onContentChange={handleContentChange}
        onCopyClick={handleCopyToClipboard}
        onSendEmail={sendEmail}
      />
    </>
  );
};

export default ShareButton;
